import Button, { ButtonTypeEnum } from '@/components/portal/ui/button'
import { useFormik } from 'formik'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import InputGroup from '../elements/InputGroup'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import request, { AxiosError } from 'axios'
import * as Yup from 'yup'

import ForgotPasswordHeader from './ForgotPasswordHeader'
import {
  AccountStatusEnum,
  cognitoErrorsIndex,
  CognitoIdentityProviderErrorEnum,
} from '@/types/cognito'
import BodyOne from '@/components/portal/ui/typography/BodyOne'
import Link from 'next/link'
import { setLoadingCopy } from '@/utils/portal/setLoadingCopy'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please use a valid email address')
    .required('Required'),
})

interface ForgotPasswordFormProps {
  setShowConfirmation: Dispatch<SetStateAction<boolean>>
  setSharedValues: Dispatch<
    SetStateAction<{
      email: string
    }>
  >
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  setShowConfirmation,
  setSharedValues,
}) => {
  const [loading, setLoading] = useState(false)
  const [buttonCopy, setButtonCopy] = useState('')
  const [error, setError] = useState('')
  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      proNumber: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      values.email = values.email.toLowerCase()
      const body = {
        ...values,
        name: `${values.firstName} ${values.lastName}`,
      }

      try {
        const response = await request.post(
          '/api/auth/forgot-password-request',
          {
            ...body,
          }
        )
        if (response?.data?.status === AccountStatusEnum.PENDING) {
          setLoading(false)
          setShowConfirmation(true)
          setSharedValues({
            email: values?.email,
          })
        } else {
          setLoading(false)
          setError(
            'There was an error processing your request. Please try again.'
          )
        }
      } catch (error: AxiosError | any) {
        setLoading(false)
        if (
          error?.response?.data?.__type &&
          error?.response?.data?.__type in cognitoErrorsIndex
        ) {
          const { __type } = error.response.data
          return setError(
            cognitoErrorsIndex[__type as CognitoIdentityProviderErrorEnum]
          )
        }
        setError(
          'There was an error processing your request. Please try again.'
        )
        console.log(error)
      }
    },
  })

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      proNumber: formik.values.proNumber.toUpperCase(),
    })
  }, [formik.values.proNumber])

  useEffect(() => {
    if (loading) {
      setLoadingCopy(setButtonCopy)
    }
  }, [loading])

  return (
    <form onSubmit={formik.handleSubmit}>
      <ForgotPasswordHeader />

      <div className="w-[369px] mx-auto mt-8">
        <InputGroup
          label="Email"
          error={formik.errors.email}
          placeholder="you@example.com"
          value={formik.values.email}
          touched={formik.touched.email}
          handleBlur={formik.handleBlur('email')}
          handleChange={formik.handleChange('email')}
        />

        <Button
          disableHoverShadow
          additionalStyles="font-ttHovesMedium"
          type={ButtonTypeEnum.submit}
          color="bg-green-100"
          borderRadius="rounded-[4px]"
          width="w-full"
          inline
        >
          {loading ? `Requesting${buttonCopy}` : 'Request'}
        </Button>

        <p className="mt-3 text-red-brand text-regular">{error}</p>

        <BodyOne
          additionalClasses="mb-8 text-center mt-8"
          fontSize="text-regular"
        >
          Have an account?{' '}
          <Link href="/login" className="text-green-portal">
            Login
          </Link>
        </BodyOne>
      </div>
    </form>
  )
}

export default ForgotPasswordForm
